import { useState } from 'react'
import { Button, Popover, theme } from 'antd'
import { SmileOutlined } from '@ant-design/icons'
import { emojiCategories } from '../../Helpers/emojis'

interface EmojiPanelProps {
  onSelect: (emoji: string) => void
  columns?: number
  emojiSize?: number
  tabSize?: number
  gridHeight?: number
}

/** Standalone emoji grid with category tabs — used inline or inside a Popover */
export function EmojiPanel({
  onSelect,
  columns = 10,
  emojiSize = 22,
  tabSize = 18,
  gridHeight = 200,
}: EmojiPanelProps) {
  const { token } = theme.useToken()
  const [activeTab, setActiveTab] = useState(0)

  return (
    <div>
      {/* Category tabs */}
      <div
        style={{
          display: 'flex',
          gap: 2,
          padding: '0 4px 6px',
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          marginBottom: 6,
        }}
      >
        {emojiCategories.map((cat, i) => (
          <div
            key={i}
            onClick={() => setActiveTab(i)}
            style={{
              flex: 1,
              textAlign: 'center',
              fontSize: tabSize,
              padding: '4px 0',
              cursor: 'pointer',
              borderRadius: 6,
              background: activeTab === i ? token.colorFillTertiary : 'transparent',
              transition: 'background 0.15s',
            }}
          >
            {cat.icon}
          </div>
        ))}
      </div>

      {/* Emoji grid */}
      <div
        className="chat-scroll"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: 2,
          maxHeight: gridHeight,
          overflowY: 'auto',
        }}
      >
        {emojiCategories[activeTab]?.emojis.map((emoji) => (
          <div
            key={emoji}
            onClick={() => onSelect(emoji)}
            style={{
              fontSize: emojiSize,
              textAlign: 'center',
              padding: '3px 0',
              cursor: 'pointer',
              borderRadius: 6,
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = token.colorFillTertiary }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
          >
            {emoji}
          </div>
        ))}
      </div>
    </div>
  )
}

interface EmojiPickerProps {
  onSelect: (emoji: string) => void
}

/** Desktop-only Popover emoji picker */
export default function EmojiPicker({ onSelect }: EmojiPickerProps) {
  const { token } = theme.useToken()
  const [open, setOpen] = useState(false)

  return (
    <Popover
      content={
        <div style={{ width: 380 }}>
          <EmojiPanel onSelect={onSelect} />
        </div>
      }
      trigger="click"
      placement="topLeft"
      open={open}
      onOpenChange={setOpen}
    >
      <Button
        type="text"
        icon={<SmileOutlined style={{ fontSize: 18 }} />}
        style={{ color: open ? token.colorPrimary : token.colorTextSecondary }}
      />
    </Popover>
  )
}
